import logo from "../images/logo.png";
import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../context/AppContext";
import { validate } from "react-email-validator";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import CookieNoticePopup from "./popups/CookieNoticePopup";

const SignIn = () => {
  const { t } = useTranslation();

  const { authenticate, saveToken, saveUser } = useContext(AppContext);

  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [value, setValue] = useState("");

  const navigate = useNavigate();

  const [showCookieNotice, setShowCookieNotice] = useState(
    !Cookies.get("cookiesAccepted")
  ); 

  const handleDismissCookieNotice = () => {
    setShowCookieNotice(false);
    Cookies.set("cookiesAccepted", "true", { expires: 1 }); 
  };
  
  const handleChange = (event) => {
    setValue(event.target.value);
  };

  useEffect(() => {
    let timer;

    if (isLoading) {
      timer = setTimeout(() => {
        setIsLoading(false);
      }, 3000);
    }

    return () => clearTimeout(timer);
  }, [isLoading]);

  const isValidPassword = (password) => {
    return password.length > 3;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const email = e.target.email.value;
    const password = e.target.password.value;

    const isValidEmail = validate(email);

    if (isValidEmail === false) {
      setErrorMessage(t("Check your Email"));
      return;
    }

    if (isValidPassword(password) === false) {
      setErrorMessage(t("Check your password"));
      return;
    }

    const authenticateUser = async (email, password, timeout = 5000) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      const statusCode = 200;

      try {
        const response = await fetch(
          `${process.env.REACT_APP_ADDRESS}/users/auth`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-status-code": statusCode.toString(),
            },
            body: JSON.stringify({
              email: email,
              password: password,
            }),
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
          if (response.status === 404) {
            setErrorMessage(t("Invalid email or password."));
          } else if (response.status === 401) {
            setErrorMessage(t("Access Denied."));
          } else if (response.status === 403) {
            Cookies.set("Recentuser", email, { expires: 1 }); // Expires in 1 day
            navigate("/verifyEmail");
          } else {
            setErrorMessage(t("Internal server error 😞"));
          }

          return;
        }

        const data = await response.json();
        console.log(data);
        if (data.token == null) {
          setErrorMessage(t("Internal server error 😞"));
        }

        setErrorMessage(null);
        setIsLoading(true);

        saveToken(data.token);
        console.log(JSON.stringify(data.user));
        saveUser(data.user);

        authenticate();
        navigate("/dashboard/profile", { replace: true });
      } catch (error) {
        if (error.name === "AbortError") {
          setErrorMessage(t("Request aborted"));
        }
        if (error.message === t("Failed to fetch")) {
          setErrorMessage(
            t("Network issue. Please check your internet connection.")
          );
        } else {
          console.error(t("Error authenticating user:"), error);
        }
      }
    };

    authenticateUser(email, password);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen pb-20">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <Link to="/" className="flex items-center justify-center">
          <img className="mx-auto h-56 w-auto" src={logo} alt="hayya logo" />
        </Link>
        <h2 className="text-center text-2xl font-extrabold leading-9 tracking-tight text-gray-900">
          {t("Sign in to your account")}
        </h2>
      </div>

      <div className="mt-3 sm:mx-auto sm:w-full sm:max-w-md">
        {errorMessage && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {errorMessage}
                </h3>
              </div>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-semibold leading-6 text-gray-900"
            >
              {t("Email address")}
            </label>
            <div className="mt-1">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-pink-600 sm:text-sm sm:leading-6"
                value={value}
                onChange={handleChange}
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label
                htmlFor="password"
                className="block text-sm font-semibold leading-6 text-gray-900"
              >
                {t("Password")}
              </label>
              <div className="text-sm">
                <Link
                  to="/forgetPassword"
                  className="font-semibold  text-pink-600 hover:text-pink-500"
                >
                  {t("Forgot password?")}
                </Link>
              </div>
            </div>
            <div className="mt-1">
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-pink-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          <div>
            {isLoading ? (
              <div className="text-center text-gray-600">{t("Loading...")}</div>
            ) : (
              <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-semibold text-white bg-pink-600 hover:bg-pink-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-600"
              >
                {t("Sign in")}
              </button>
            )}
          </div>
        </form>

        <p className="mt-6 text-center text-sm text-gray-500">
          {t("Not a member?")}{" "}
          <Link
            to="/signup"
            className="font-semibold leading-6 text-pink-600 hover:text-pink-500"
          >
            {t("Sign up")}
          </Link>
        </p>
      </div>
      {showCookieNotice && (
<CookieNoticePopup onDismiss={handleDismissCookieNotice} />
      )}
    </div>
  );
};

export default SignIn;
